import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_SELECTION,
  readSelectionFromSearch,
  readStoredSelection,
  resolveRange,
  storeSelection,
  writeSelectionToSearch,
  type PeriodSelection,
} from './period';
import type { PeriodRange } from '../types';

/**
 * Periodo compartilhado por toda a arvore autenticada (RNF15).
 *
 * A fonte da verdade e a URL: mudar o filtro navega (replace) para a mesma
 * rota com `?periodo=` ou `?de=&ate=`, e todo consumidor le dali. Assim o
 * botao "voltar" nao desfaz o filtro em passos, o link e compartilhavel e o
 * F5 preserva a janela. O localStorage so entra quando a URL nao diz nada.
 */

interface PeriodContextValue {
  readonly selection: PeriodSelection;
  readonly range: PeriodRange;
  setSelection(selection: PeriodSelection): void;
}

const PeriodContext = createContext<PeriodContextValue | null>(null);

export function PeriodProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const selection = useMemo<PeriodSelection>(
    () => readSelectionFromSearch(location.search) ?? readStoredSelection() ?? DEFAULT_SELECTION,
    [location.search],
  );

  // `range` e derivado do `selection` e do relogio; recalcular por render seria
  // barato, mas a identidade estavel e o que evita refetch a cada render.
  const range = useMemo(() => resolveRange(selection), [selection]);

  const setSelection = useCallback(
    (next: PeriodSelection): void => {
      storeSelection(next);
      navigate(
        { pathname: location.pathname, search: writeSelectionToSearch(location.search, next) },
        { replace: true },
      );
    },
    [navigate, location.pathname, location.search],
  );

  const value = useMemo<PeriodContextValue>(() => ({ selection, range, setSelection }), [selection, range, setSelection]);

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodContextValue {
  const value = useContext(PeriodContext);
  if (value === null) {
    throw new Error('usePeriod precisa estar dentro de <PeriodProvider>');
  }
  return value;
}
